import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import MainCard from 'ui-component/cards/MainCard';
import axiosServices from 'utils/axios';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import CreateComplexModal from './CreateComplexModal';
import ViewComplexUsersModal from './ViewComplexUsersModal';

// ----- Table Sorting Utilities -----
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

const getComparator = (order, orderBy) =>
    order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);

function stableSort(array, comparator) {
    const stabilizedArray = array.map((el, index) => [el, index]);
    stabilizedArray.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedArray.map((el) => el[0]);
}

// ----- Table Header Definition -----
const headCells = [
    { id: 'name', numeric: false, label: 'Complex Name', align: 'left' },
    { id: 'users', numeric: false, label: 'Users', align: 'left' },
    { id: 'createdAt', numeric: false, label: 'Created Date', align: 'left' }
];

// ----- Enhanced Table Head Component -----
function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.align} sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell align="center" sx={{ pr: 3 }}>
                    Action
                </TableCell>
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    onRequestSort: PropTypes.func.isRequired
};

// ----- Manage Complexes Page -----
export default function ManageComplexes() {
    const theme = useTheme();
    const [complexes, setComplexes] = useState([]);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [openComplexModal, setOpenComplexModal] = useState(false);
    const [editComplex, setEditComplex] = useState(null);
    const [openViewUsersModal, setOpenViewUsersModal] = useState(false);
    const [viewUsers, setViewUsers] = useState([]);

    const fetchComplexes = async () => {
        try {
            const { data } = await axiosServices.get('/complex', {
                headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
            });
            // Assuming the response structure: { complexes: Complex[], total: number }
            setComplexes(data.complexes || []);
        } catch (error) {
            toast.error('Error fetching complexes');
        }
    };

    // Fetch complexes from backend on mount
    useEffect(() => {
        fetchComplexes();
    }, []);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event) => {
        setSearch(event.target.value);
    };

    // Filter complexes based on search text
    const filteredComplexes = complexes.filter(
        (complex) =>
            (complex.name && complex.name.toLowerCase().includes(search.toLowerCase())) ||
            (complex.address && complex.address.toLowerCase().includes(search.toLowerCase()))
    );

    const sortedComplexes = stableSort(filteredComplexes, getComparator(order, orderBy));
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sortedComplexes.length) : 0;

    // Action handlers
    const handleEdit = (complex) => {
        setEditComplex(complex);
        setOpenComplexModal(true);
    };

    const handleDelete = async (id) => {
        try {
            await axiosServices.delete(`/complex/${id}`, {
                headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
            });
            toast.success('Complex deleted successfully');
            fetchComplexes();
        } catch (error) {
            toast.error('Error deleting complex');
        }
    };

    console.log(sortedComplexes);

    return (
        <>
            <CreateComplexModal
                open={openComplexModal}
                setOpen={setOpenComplexModal}
                editComplex={editComplex}
                setEditComplex={setEditComplex}
                fetchComplexes={fetchComplexes}
            />
            <ViewComplexUsersModal open={openViewUsersModal} onClose={() => setOpenViewUsersModal(false)} users={viewUsers} />
            <MainCard title="Manage Complexes" content={false}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Search Complex..."
                                value={search}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" justifyContent="flex-end" alignItems="center">
                                <Button variant="contained" onClick={() => setOpenComplexModal(true)}>
                                    Create Complex
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
                <TableContainer>
                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                        <TableBody>
                            {sortedComplexes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((complex) => (
                                <TableRow hover key={complex.id}>
                                    <TableCell align="left">{complex.name}</TableCell>
                                    <TableCell align="left">
                                        {complex.complexUsers && complex.complexUsers.length > 0 ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2">
                                                    {complex.complexUsers
                                                        .slice(0, 2)
                                                        .map((cu) => `${cu.user.firstName} ${cu.user.lastName}`)
                                                        .join(', ')}
                                                </Typography>
                                                {complex.complexUsers.length > 2 && (
                                                    <Button
                                                        variant="text"
                                                        sx={{ color: 'blue', textTransform: 'none' }}
                                                        onClick={() => {
                                                            setViewUsers(complex.complexUsers);
                                                            setOpenViewUsersModal(true);
                                                        }}
                                                    >
                                                        View More
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                No Users Added Yet
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="left">{new Date(complex.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell align="center" sx={{ pr: 3 }}>
                                        <Tooltip title="Edit">
                                            <IconButton color="primary" onClick={() => handleEdit(complex)}>
                                                <EditTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton color="error" onClick={() => handleDelete(complex.id)}>
                                                <DeleteIcon sx={{ fontSize: '1.3rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={4} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={sortedComplexes.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </MainCard>
        </>
    );
}
